// CSV Parser utility for contact imports

export interface ParsedCSV {
  headers: string[];
  rows: string[][];
  rowCount: number;
}

export interface CSVParseError {
  row: number;
  message: string;
}

export interface CSVParseResult {
  success: boolean;
  data?: ParsedCSV;
  errors?: CSVParseError[];
}

// Parse a CSV file and return headers + rows
export function parseCSV(content: string): CSVParseResult {
  const errors: CSVParseError[] = [];

  // Normalize line endings
  const normalized = content.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
  const lines = normalized.split('\n').filter(line => line.trim());

  if (lines.length === 0) {
    return { success: false, errors: [{ row: 0, message: 'File is empty' }] };
  }

  // Parse header row
  const headers = parseCSVRow(lines[0]);

  if (headers.length === 0) {
    return { success: false, errors: [{ row: 0, message: 'No headers found' }] };
  }

  // Parse data rows
  const rows: string[][] = [];
  for (let i = 1; i < lines.length; i++) {
    const row = parseCSVRow(lines[i]);

    // Pad or trim row to match header count
    while (row.length < headers.length) {
      row.push('');
    }
    if (row.length > headers.length) {
      row.length = headers.length;
    }

    rows.push(row);
  }

  return {
    success: true,
    data: {
      headers,
      rows,
      rowCount: rows.length,
    },
    errors: errors.length > 0 ? errors : undefined,
  };
}

// Parse a single CSV row, handling quoted values
function parseCSVRow(line: string): string[] {
  const result: string[] = [];
  let current = '';
  let inQuotes = false;

  for (let i = 0; i < line.length; i++) {
    const char = line[i];
    const nextChar = line[i + 1];

    if (inQuotes) {
      if (char === '"' && nextChar === '"') {
        // Escaped quote
        current += '"';
        i++;
      } else if (char === '"') {
        // End of quoted field
        inQuotes = false;
      } else {
        current += char;
      }
    } else {
      if (char === '"') {
        // Start of quoted field
        inQuotes = true;
      } else if (char === ',') {
        // Field separator
        result.push(current.trim());
        current = '';
      } else {
        current += char;
      }
    }
  }

  // Don't forget the last field
  result.push(current.trim());

  return result;
}

// Contact field definitions for mapping
export const CONTACT_FIELDS = [
  { key: 'first_name', label: 'First Name', required: true },
  { key: 'last_name', label: 'Last Name', required: false },
  { key: 'email', label: 'Email', required: false },
  { key: 'phone', label: 'Phone', required: false },
  { key: 'company', label: 'Company', required: false },
  { key: 'stage', label: 'Stage', required: false },
  { key: 'contact_type', label: 'Contact Type', required: false },
  { key: 'source', label: 'Source', required: false },
  { key: 'address_street', label: 'Street Address', required: false },
  { key: 'address_city', label: 'City', required: false },
  { key: 'address_state', label: 'State', required: false },
  { key: 'address_zip', label: 'ZIP Code', required: false },
  { key: 'birthday', label: 'Birthday', required: false },
  { key: 'notes', label: 'Notes', required: false },
  { key: 'tags', label: 'Tags', required: false },
] as const;

export type ContactFieldKey = typeof CONTACT_FIELDS[number]['key'];

// Try to auto-detect column mappings based on header names
export function autoDetectMappings(headers: string[]): Record<number, ContactFieldKey | null> {
  const mappings: Record<number, ContactFieldKey | null> = {};

  const patterns: Record<ContactFieldKey, RegExp[]> = {
    first_name: [/^first[\s_-]?name$/i, /^fname$/i, /^first$/i, /^given[\s_-]?name$/i],
    last_name: [/^last[\s_-]?name$/i, /^lname$/i, /^last$/i, /^surname$/i, /^family[\s_-]?name$/i],
    email: [/^e?[\s_-]?mail$/i, /^email[\s_-]?address$/i],
    phone: [/^phone$/i, /^phone[\s_-]?number$/i, /^mobile$/i, /^cell$/i, /^telephone$/i],
    company: [/^company$/i, /^organization$/i, /^org$/i, /^employer$/i, /^business$/i],
    stage: [/^stage$/i, /^status$/i, /^pipeline$/i],
    contact_type: [/^type$/i, /^contact[\s_-]?type$/i, /^category$/i],
    source: [/^source$/i, /^lead[\s_-]?source$/i, /^how[\s_-]?met$/i, /^referral$/i],
    address_street: [/^street$/i, /^address$/i, /^street[\s_-]?address$/i, /^address[\s_-]?1$/i],
    address_city: [/^city$/i, /^town$/i],
    address_state: [/^state$/i, /^province$/i, /^region$/i],
    address_zip: [/^zip$/i, /^zip[\s_-]?code$/i, /^postal$/i, /^postal[\s_-]?code$/i],
    birthday: [/^birthday$/i, /^birth[\s_-]?date$/i, /^dob$/i, /^date[\s_-]?of[\s_-]?birth$/i],
    notes: [/^notes?$/i, /^comments?$/i, /^description$/i],
    tags: [/^tags?$/i, /^labels?$/i, /^categories$/i],
  };

  headers.forEach((header, index) => {
    let matched = false;

    for (const [field, regexList] of Object.entries(patterns)) {
      if (regexList.some(regex => regex.test(header))) {
        mappings[index] = field as ContactFieldKey;
        matched = true;
        break;
      }
    }

    if (!matched) {
      mappings[index] = null;
    }
  });

  return mappings;
}

// Convert a row to a contact object based on mappings
export function rowToContact(
  row: string[],
  mappings: Record<number, ContactFieldKey | null>
): Record<string, string> {
  const contact: Record<string, string> = {};

  Object.entries(mappings).forEach(([indexStr, field]) => {
    if (field) {
      const index = parseInt(indexStr, 10);
      const value = row[index]?.trim() || '';
      if (value) {
        contact[field] = value;
      }
    }
  });

  return contact;
}

// Validate a contact has required fields
export function validateContact(contact: Record<string, string>): string[] {
  const errors: string[] = [];

  if (!contact.first_name) {
    errors.push('First name is required');
  }

  // Validate email format if provided
  if (contact.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(contact.email)) {
    errors.push('Invalid email format');
  }

  // Validate phone format if provided (basic check)
  if (contact.phone && !/^[\d\s\-\+\(\)\.]+$/.test(contact.phone)) {
    errors.push('Invalid phone format');
  }

  return errors;
}

// Format phone number consistently
export function formatPhone(phone: string): string {
  // Remove all non-digits
  const digits = phone.replace(/\D/g, '');

  // Format as (XXX) XXX-XXXX for US numbers
  if (digits.length === 10) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  }

  // Format with country code
  if (digits.length === 11 && digits.startsWith('1')) {
    return `+1 (${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
  }

  // Return as-is if format is unknown
  return phone;
}
